<template>
    <modal-dialog
        id="login-dialog"
        :show.sync="show"
        :closable="false"
        :panel_ids="['login','lost-password']"
        :current_panel_id.sync="current_panel_id"
        :class="{'nibnut-logo-header': !!$root.application_logo}"
        class="nibnut-flippable"
    >
        <div slot="panel-header">
            <div :class="{'text-center': !tight_fit, 'text-left': tight_fit}">
                <div :class="{'py-4': !tight_fit, 'float-right': tight_fit}" class="px-2 nibnut-logo-holder">
                    <img v-if="setting('application_logo')" :src="setting('application_logo')" class="brand-logo">
                    <span v-else>{{ setting('application_name') }}</span>
                </div>

                <span v-if="fetching">{{ $root.translate("Fetching invitation...") }}</span>
                <span v-else-if="(current_panel_id === 'login') && (current_tab === 'register')">{{ $root.translate("Sign Up") }}</span>
                <span v-else-if="current_panel_id==='lost-password'">{{ $root.translate("Lost My Password") }}</span>
                <span v-else>{{ $root.translate("Login") }}</span>
            </div>
        </div>
        <template slot="panel-body">
            <div v-if="fetching" class="text-center">
                <div class="loading loading-lg"></div>
            </div>
            <form
                v-else-if="current_panel_id === 'lost-password'"
                id="lost-password-form"
                role="form"
                class="tab-panel"
                @submit.prevent.stop="reset"
            >
                <form-input
                    v-model="email"
                    ref="login_email"
                    name="email"
                    type="email"
                    :live="false"
                    :error="has_error('email')"
                    :placeholder="$root.translate('Email')"
                >
                    <template v-slot:label>
                        {{ $root.translate('Email') }}
                    </template>
                </form-input>

                <loading-button
                    type="submit"
                    :loading="loading"
                    class="btn-block btn-primary mt-8"
                >
                    {{ $root.translate("Reset My Password") }}
                </loading-button>
            </form>
            <form
                v-else
                id="login-form"
                role="form"
                @submit.prevent.stop="login"
            >
                <ul v-if="invitation_token" class="tab">
                    <li
                        :class="{ active: current_tab === 'login' }"
                        class="tab-item"
                    >
                        <a
                            href="#"
                            @click.prevent="current_tab = 'login'"
                        >
                            {{ $root.translate("Login") }}
                        </a>
                    </li>
                    <li
                        :class="{ active: current_tab === 'register' }"
                        class="tab-item"
                    >
                        <a
                            href="#"
                            @click.prevent="current_tab = 'register'"
                        >
                            {{ $root.translate("Signup") }}
                        </a>
                    </li>
                </ul>
                <div v-if="invitation_token && (current_tab === 'register')" class="tab-panel">
                    <div class="columns">
                        <div class="column col-6 col-sm-12 mb-2">
                            <form-input
                                ref="login_organization"
                                v-model="organization"
                                name="organization"
                                :error="has_error('organization')"
                                :placeholder="$root.translate('Organization')+' ('+$root.translate('required')+')'"
                                required
                            >
                                <template v-slot:label>
                                    {{ $root.translate('Organization') }}
                                </template>
                            </form-input>
                        </div>
                        <div class="column col-6 col-sm-12 mb-2">
                            <div class="form-group">
                                <label class="form-label">
                                    {{ $root.translate('Country') }}
                                </label>
                                <country-picker
                                    v-model="country_code"
                                    class="mb-2"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column col-6 col-sm-12 mb-2">
                            <form-input
                                ref="login_first_name"
                                v-model="first_name"
                                name="first_name"
                                :error="has_error('first_name')"
                                :placeholder="$root.translate('First Name')+' ('+$root.translate('required')+')'"
                                required
                            >
                                <template v-slot:label>
                                    {{ $root.translate('First Name') }}
                                </template>
                            </form-input>
                        </div>
                        <div class="column col-6 col-sm-12 mb-2">
                            <form-input
                                ref="login_last_name"
                                v-model="last_name"
                                name="last_name"
                                :error="has_error('last_name')"
                                :placeholder="$root.translate('Last Name')+' ('+$root.translate('required')+')'"
                                required
                            >
                                <template v-slot:label>
                                    {{ $root.translate('Last Name') }}
                                </template>
                            </form-input>
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column col-6 col-sm-12 mb-2">
                            <form-input
                                ref="login_email"
                                v-model="email"
                                name="email"
                                type="email"
                                :error="has_error('email')"
                                :editable="!invitation_data"
                                :placeholder="$root.translate('Email')+' ('+$root.translate('required')+')'"
                                required
                            >
                                <template v-slot:label>
                                    {{ $root.translate('Email') }}
                                </template>
                            </form-input>
                        </div>
                        <div class="column col-6 col-sm-12 mb-2">
                            <form-input
                                ref="login_password"
                                v-model="password"
                                name="password"
                                type="password"
                                :error="has_error('password')"
                                :placeholder="$root.translate('Password')"
                                required
                            >
                                <template v-slot:label>
                                    {{ $root.translate('Password') }}
                                </template>
                            </form-input>
                        </div>
                    </div>

                    <package-picker
                        v-model="package_ids"
                        class="mt-8"
                    />

                    <div v-if="!!already_invited" class="toast toast-error mt-4 text-center">
                        {{ already_invited }}
                        <br />
                        <small><a href @click.prevent="resend_invitation">({{ $root.translate("Please resend me my invitation link...") }})</a></small>
                    </div>

                    <loading-button type="submit" :disabled="!registration_ready" :loading="loading" @click.prevent="register" class="btn-block mt-8">
                        {{ $root.translate("Sign Up") }}<open-icon glyph="sign-in-alt" class="ml-2" />
                    </loading-button>
                </div>
                <div v-else class="tab-panel">
                    <form-input
                        ref="login_email"
                        v-model="email"
                        name="email"
                        type="email"
                        :live="false"
                        :error="has_error('email')"
                        :placeholder="$root.translate('Email')"
                    >
                        <template v-slot:label>
                            {{ $root.translate('Email') }}
                        </template>
                    </form-input>

                    <form-input
                        ref="login_password"
                        v-model="password"
                        name="password"
                        type="password"
                        :live="false"
                        :error="has_error('password')"
                        :placeholder="$root.translate('Password')"
                    >
                        <template v-slot:label>
                            {{ $root.translate('Password') }}
                        </template>
                    </form-input>

                    <div class="columns text-gray mb-8">
                        <div class="column col-6">
                            <switch-input
                                name="remember"
                                :checked="!!remember"
                                @input="remember = !remember"
                            >
                                {{ $root.translate('Remember me') }}
                            </switch-input>
                        </div>
                        <div class="column col-6 text-right">
                            <a @click.prevent="current_panel_id = 'lost-password'" class="btn btn-link mt-1">
                                <open-icon glyph="lock" class="mr-2" />{{ $root.translate("Shoot! Forgot my password...") }}
                            </a>
                        </div>
                    </div>

                    <loading-button
                        type="submit"
                        :loading="loading"
                        class="btn-block btn-primary"
                    >
                        {{ $root.translate("Login") }}<open-icon glyph="sign-in-alt" class="ml-2" />
                    </loading-button>
                </div>
            </form>
        </template>
        <div v-if="!fetching&&!invitation_data" slot="panel-footer" class="text-center">
            <a v-if="invitation_token && (current_panel_id === 'login') && (current_tab === 'register')" @click.prevent="goto_login('login')" class="btn btn-link">
                <small>{{ $root.translate("I'm already signed up - let me log in instead...") }}</small>
            </a>
            <a v-else-if="current_panel_id === 'lost-password'" @click.prevent="goto_login('login')" class="btn btn-link">
                <small>{{ $root.translate("Got it! Just found my password!") }}</small>
            </a>
            <div v-else-if="invitation_token">
                <a @click.prevent="goto_login('register')" class="btn btn-link">
                    <small>{{ $root.translate("I'm new here - let me sign up!") }}</small>
                </a>
            </div>
            <div v-if="app_version" class="app-version-indicator">({{ app_version }})</div>
        </div>
    </modal-dialog>
</template>

<script type="text/javascript">
import PackagePicker from "../ui/PackagePicker"
import CountryPicker from "../ui/CountryPicker"
import { OpenIcon } from "../nibnut/components"
import { ModalDialog, FormInput, SwitchInput, LoadingButton } from "../library"

import { handles_errors } from "../nibnut/mixins"
import utilities from "../mixins/Is"
import addl_string_utilities from "../mixins/AddlStringUtilities"

export default {
    mounted () {
        if(!this.load_invitation()) {
            if(this.$cookie.get("tsc-user")) {
                this.current_panel_id = "login"
                this.current_tab = "login"
            }
            if(!this.country_code) this.country_code = this.setting("default_country_code")
        }
    },
    watch: {
        current_panel_id: "panel_changed"
    },
    mixins: [utilities, handles_errors, addl_string_utilities],
    components: {
        PackagePicker,
        CountryPicker,
        FormInput,
        SwitchInput,
        ModalDialog,
        LoadingButton,
        OpenIcon
    },
    methods: {
        goto_login (tab_id) {
            this.current_panel_id = "login"
            this.current_tab = tab_id
            if(!this.invitation_token && (this.current_tab === "register")) this.current_tab = "login"
        },
        error (error, header, message) {
            if(error && error.message && error.message.message) this.$error(error.message.message || message)
            else if(error && error.message) this.$error(error.message || message)
            else this.$error(message)
        },
        panel_changed () {
            this.clear_all_errors()
        },
        load_invitation () {
            const token = this.invitation_token
            if(token) {
                this.fetching = true
                this.current_panel_id = "register"
                this.$store.dispatch("LOAD_INVITATION", {
                    token
                }).then(invitation_data => {
                    this.invitation_data = invitation_data
                    this.invitation_data.token = token
                    this.organization = invitation_data.organization
                    this.first_name = invitation_data.first_name
                    this.last_name = invitation_data.last_name
                    this.email = invitation_data.email
                    this.country_code = invitation_data.country_code
                }).catch(error => {
                    if(error) this.error(null, error.header, error.message)
                }).then(() => {
                    this.fetching = false
                })
                return true
            }
            return false
        },
        grab_autofilled_value (ref, default_value) {
            const fields = this.$refs[ref] ? this.$refs[ref].$el.getElementsByTagName("input") : []
            const field = fields.length ? fields[0] : {}
            return field.value || default_value
        },
        login () {
            setTimeout(() => {
                this.loading = true
                this.$store.dispatch("LOGIN", {
                    email: this.grab_autofilled_value("login_email", this.email),
                    password: encodeURIComponent(this.grab_autofilled_value("login_password", this.password))
                }).then(() => {
                    this.password = ""
                    this.$store.dispatch("UNREQUEST_LOGIN")
                }).catch(this.receive_error).then(() => {
                    this.loading = false
                })
            }, 500) // need a timeout slightly longer than input field's debounce. Otherwise, Enter key happens before this.field is updated!
        },
        register () {
            if(!this.invitation_token) return
            this.clear_all_errors()
            this.loading = true

            this.$store.dispatch("SIGNUP", {
                data: {
                    first_name: this.grab_autofilled_value("login_first_name", this.first_name),
                    last_name: this.grab_autofilled_value("login_last_name", this.last_name),
                    organization: this.grab_autofilled_value("login_organization", this.organization),
                    country_code: this.country_code,
                    email: this.grab_autofilled_value("login_email", this.email),
                    password: encodeURIComponent(this.grab_autofilled_value("login_password", this.password)),
                    package_ids: this.package_ids,
                    invitation_token: this.invitation_data ? this.invitation_data.token : null
                }
            }).then(() => {
                this.current_panel_id = "login"
                this.first_name = ""
                this.last_name = ""
                this.organization = ""
                this.country_code = this.setting("default_country_code")
                this.password = ""
                this.$store.dispatch("UNREQUEST_LOGIN")
            }).catch(error => {
                if(error) {
                    if(error.topic_id && (error.topic_id === "invitation_token")) {
                        this.already_invited = error.message
                    } else {
                        if(error.topic_id) this.add_error(error.topic_id, error.message || this.$root.translate("Invalid email or password."))
                        this.error(error, this.$root.translate("Registration Failed!"), error.message || this.$root.translate("Invalid email or password."))
                    }
                }
            }).then(() => {
                this.loading = false
            })
        },
        reset () {
            setTimeout(() => {
                this.loading = true
                this.$store.dispatch("SEND_PASSWORD_RESET", { email: this.email })
                    .then(response => {
                        this.$success(response.message)
                        this.flip()
                        this.goto_login("login")
                    }).catch(error => {
                        this.$error(error.message)
                    }).then(() => {
                        this.loading = false
                    })
            }, 500)
        },
        submit () {
            if(this.flipped) return this.reset()
            return this.login()
        },
        flip () {
            this.flipped = !this.flipped
        },
        resend_invitation () {
            if(this.email) {
                this.$store.dispatch("RECORDS_ACTION", { entity: "user", action: "re-invite", data: { email: this.email }, passthru: true }).then(() => {
                    this.$success(this.$root.translate("Check your inbox... (or junk folder, just in case)"))
                }).catch(error => {
                    this.$error(error.message)
                })
            }
        }
    },
    computed: {
        invitation_token () {
            if(window.location.search) {
                const matches = window.location.search.match(/(?:\?|&)invitation=([^&]+)(?:&|$)/)
                if(matches) return matches[1]
            }
            return null
        },
        registration_ready () {
            return !!this.package_ids.length
        }
    },
    props: [
        "show"
    ],
    data () {
        return {
            current_panel_id: "login",
            current_tab: "register",
            organization: "",
            first_name: "",
            last_name: "",
            email: "",
            password: "",
            country_code: "",
            package_ids: [],
            invitation_data: null,
            remember: false,
            flipped: false,
            loading: false,
            fetching: false,
            already_invited: false
        }
    }
}
</script>
